import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CalendarIcon,
  CameraIcon,
  CopyIcon,
  DotsVerticalIcon,
  DownloadIcon,
  FrameIcon,
  ImageIcon,
  InfoCircledIcon,
  TransformIcon
} from '@radix-ui/react-icons';
import {
  Button,
  DropdownMenu,
  EmptyState,
  Flex,
  Spinner,
  Text,
  toast
} from '@raystack/apsara';
import { createFormattedAreaEl } from '@studio/map';
import clsx from 'clsx';

import { CreateNewProjectPopup } from '@src/components/CreateNewProject';
import { copyTextToClipboard } from '@src/components/Custom/Copy/CopyText';
import ImageDivThumb from '@src/containers/Explore/subComponents/Table/ImageDiv/ImageThumbnail';
import { ProjectsProvider } from '@src/contexts';
import { For, Show } from '@src/hoc';
import { useOrderItemInCartList } from '@src/hooks';
import { ReactComponent as SatelliteIcon } from '@src/icons/satellite.svg';
import { InputRasterStacThumbnailHandler } from '@src/utils/MapUtils';
import { getUSTimeFormat } from '@src/utils/dateUtils';

import { WithAddToCartImageThumbnail } from './WithAddToCartImageThumbnail';
import { AddToCartPopup } from './WithAddToCartImageThumbnail/AddToCartPopup';
import { ReactComponent as CloudIcon } from './cloud.svg';

import styles from './NewTileView.module.css';

export async function downloadImage(image) {
  toast.message('Preparing your resource for download...', {
    icon: <Spinner size={2} />,
    id: 'downloading-thumbnail-toast'
  });
  try {
    const imageUrl = await fetch(
      InputRasterStacThumbnailHandler(image.url || image.preview_url),
      { credentials: 'include' }
    );

    const imageBlog = await imageUrl.blob();
    const imageURL = URL.createObjectURL(imageBlog);

    const link = document.createElement('a');
    link.href = imageURL;
    link.download = image.id;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    // If response status is 5xx, or 4xx return error
    if (error.status % 100 === 5 || error.status % 100 === 4) {
      const err = 'Failed to download image.';
      toast.dismiss('downloading-thumbnail-toast');
      toast.error('Something went wrong!', { description: err });
      console.error(err);
      return;
    }
  }
}

const TileItem = ({
  image,
  onViewMoreClick,
  tiles,
  handleSelect,
  prices,
  aoiGeometry,
  onMouseEnter,
  onMouseLeave
}) => {
  const projects = useSelector(state => state.projects.projectList);
  const { is_accessible } = image;
  const [open, setOpen] = useState(false);

  const metadata = [
    {
      icon: CalendarIcon,
      selector: image =>
        image.datetime ? getUSTimeFormat(image.datetime) : null,
      showDropDown: true
    },
    { icon: SatelliteIcon, selector: image => image.name },
    { icon: CameraIcon, selector: image => image.satellite },
    {
      icon: FrameIcon,
      selector: image => createFormattedAreaEl(image.area)
    },
    {
      icon: CloudIcon,
      selector: image =>
        image.cloud || image.cloud === 0 ? `${image.cloud.toFixed(2)}%` : 'N/A'
    }
  ];

  const actions = [
    {
      label: 'Download thumbnail',
      icon: DownloadIcon,
      click: downloadImage
    },
    {
      label: 'Copy image ID',
      icon: CopyIcon,
      click: image =>
        copyTextToClipboard(image.id, val => {
          if (val) {
            toast.success('Image ID copied to clipboard!');
          }
        })
    },
    {
      label: 'View more information',
      icon: InfoCircledIcon,
      click: onViewMoreClick
    }
  ];

  function handleClick(event, onClick) {
    event.stopPropagation();
    return onClick;
  }

  const {
    orderItems,
    isLoading: isOrderItemInCartLoading,
    isValidating: isOrderItemInCartValidating
  } = useOrderItemInCartList();

  const orderItem = orderItems.find(d =>
    d.images?.find(e => e.image_id === image.id)
  );

  const isInCart = !!orderItem;

  const createNewProjectPopupRef = useRef(null);

  return (
    <>
      <Flex
        direction='row'
        data-test-id={`click-image-${image.id}`}
        key={image.id}
        className={`${styles.ImageDiv} ${
          tiles.some(tile => tile.id === image.id)
            ? styles.selectedImageDiv
            : ''
        }`}
        onClick={() => handleSelect(image)}
        onMouseEnter={() => {
          onMouseEnter(image.geometry);
        }}
        onMouseLeave={onMouseLeave}
      >
        <WithAddToCartImageThumbnail
          is_accessible={is_accessible}
          isInCart={isInCart}
          orderId={orderItem?.id}
          isCartLoading={
            isOrderItemInCartLoading || isOrderItemInCartValidating
          }
          classes={{
            visible: clsx(
              styles.addToCartThumbnailContent,
              isInCart && styles.removeFromCartThumbnailContent
            )
          }}
          setOpen={setOpen}
          price={prices?.[image.id]?.total}
        >
          <ImageDivThumb
            src={InputRasterStacThumbnailHandler(
              image.url || image.preview_url
            )}
            className={styles.imageDivThumb}
          />
        </WithAddToCartImageThumbnail>

        <Flex
          direction='column'
          justify='between'
          style={{ padding: 'var(--rs-space-4)', flex: 1 }}
        >
          {metadata.map((d, i) => (
            <Flex
              key={i}
              justify='between'
              onClick={event =>
                d.onClick && handleClick(event, d.onClick(image))
              }
              data-test-id={`${image.id}-click`}
              className={clsx(d.onClick && styles.clickableCell)}
            >
              <Flex gap='small' align='center'>
                <d.icon className={styles.icon} />
                <Text size={1}>{d.selector(image)}</Text>
              </Flex>
              <Show when={d?.showDropDown}>
                <DropdownMenu placement='bottom-end'>
                  <DropdownMenu.Trigger asChild>
                    <DotsVerticalIcon />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    <DropdownMenu.Group>
                      <For each={actions}>
                        {(d, index) => (
                          <DropdownMenu.Item
                            key={index}
                            data-test-id='menu-item'
                            onClick={e => {
                              e.stopPropagation();
                              d.click(image);
                            }}
                            leadingIcon={<d.icon />}
                          >
                            {d.label}
                          </DropdownMenu.Item>
                        )}
                      </For>
                    </DropdownMenu.Group>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </Show>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <ProjectsProvider>
        <CreateNewProjectPopup
          ref={createNewProjectPopupRef}
          setShowDialog={setOpen}
        />

        <AddToCartPopup
          open={open}
          setOpen={setOpen}
          id={image.id}
          price={prices?.[image.id]?.total}
          projects={projects}
          geometry={{
            type: 'FeatureCollection',
            features: [
              {
                geometry: {
                  coordinates: aoiGeometry.coordinates,
                  type: aoiGeometry.type
                },
                type: 'Feature'
              }
            ]
          }}
          setShowCreateProject={createNewProjectPopupRef.current?.setOpen}
        />
      </ProjectsProvider>
    </>
  );
};

export const NewTileView = ({
  content,
  tiles,
  handleSelect,
  showSelected,
  handleDrawing,
  handleUpload,
  onViewMoreClick,
  prices,
  aoiGeometry,
  onMouseEnter,
  onMouseLeave
}) => {
  const emptyStateMeta =
    Object.keys(aoiGeometry).length < 1
      ? {
          icon: <TransformIcon />,
          headerText: 'Looking for images in this area?',
          subHeaderText: 'Draw your area of interest to find images',
          primaryButton: (
            <Button
              variant='outline'
              color='neutral'
              onClick={() => {
                handleDrawing();
              }}
              data-test-id='empty-state-btn-primary'
            >
              Draw area of interest
            </Button>
          ),
          secondaryButton: (
            <Button
              variant='text'
              color='neutral'
              style={{ color: 'var(--rs-color-foreground-accent-primary)' }}
              onClick={handleUpload}
              data-test-id='empty-state-btn-secondary'
            >
              or upload AOI file
            </Button>
          )
        }
      : !showSelected
        ? {
            icon: <ImageIcon />,
            headerText: 'No images found!',
            subHeaderText:
              'Your filters have returned no images Check your parameters and please try again!'
          }
        : {
            icon: <ImageIcon />,
            headerText: 'No image selected',
            subHeaderText:
              'Please select the image you want to use for this AOI'
          };

  return (
    <Show
      when={content.length > 0}
      fallback={
        <Show when={emptyStateMeta.icon}>
          <EmptyState
            icon={emptyStateMeta.icon}
            heading={emptyStateMeta.headerText}
            subHeading={emptyStateMeta.subHeaderText}
            primaryAction={emptyStateMeta.primaryButton}
            secondaryAction={emptyStateMeta.secondaryButton}
          />
        </Show>
      }
    >
      <Flex direction='column' gap='small'>
        {content.map(image => (
          <TileItem
            image={image}
            key={image.id}
            {...{ tiles, handleSelect, onViewMoreClick, prices }}
            aoiGeometry={aoiGeometry}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        ))}
      </Flex>
    </Show>
  );
};

export default NewTileView;
